import React, { ReactNode, useCallback, useRef, useState } from "react";

import { faLocationArrow, faLock } from "@fortawesome/pro-solid-svg-icons";
import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";

import Button from "../../../../Button";
import { useConfigProvider } from "../../../../ConfigProvider";
import LoadingSpinner from "../../../../LoadingSpinner";
import layout from "../../../../styles/layout";
import InlineButtons from "../../../inline/InlineButtons";
import InlineLabeled, { InlineLabeledProps } from "../../../inline/InlineLabeled";
import InlineTextField from "../../../inline/InlineTextField";
import InlineSelectField from "../../../inline/SelectField";
import useCountryListOptions from "../../utils/useCountryListOptions";

import RegionField from "./RegionField";
import useManualAddressState from "./useManualAddressState";

const block = bem("mmc-inline-manual-address");

interface InlineManualAddressProps extends Omit<InlineLabeledProps, "children" | "extra"> {
  autoDetectCountry?: boolean;
  defaultEditing?: boolean;
  disabled?: boolean;
  extra?: ReactNode;
  onChange?: (address?: Address) => void;
  onReverseGeocodeAddress: (payload: {
    callback: (result: GeocodeResult) => void;
    coordinates: LongLat;
    failureCallback?: () => void;
  }) => void;
  onToggleManualAddress: () => void;
  value?: Address;
}

const ManualAddress = ({
  autoDetectCountry,
  defaultEditing = true,
  disabled,
  extra,
  label,
  labelClassName,
  labelPosition = "side",
  onChange,
  onReverseGeocodeAddress,
  onToggleManualAddress,
  required,
  value,
}: InlineManualAddressProps) => {
  const { formatMessage } = useConfigProvider();
  const {
    address,
    canClearCountry,
    findMeLoading,
    handleAddressChange,
    handleCancel,
    handleCityChange,
    handleCountryCodeChange,
    handleFindMyLocation,
    handlePostalCodeChange,
    handleRegionChange,
    handleSave,
    isAddressChanged,
  } = useManualAddressState({ autoDetectCountry, onChange, onReverseGeocodeAddress, value });

  // The whole address block shares a single editing state: clicking any
  // read-only field opens every field at once (so Tab can walk them), and the
  // footer's Cancel/Save collapse them all back to the read-only view.
  const [editing, setEditing] = useState(defaultEditing);
  const hasOpenedViaClickRef = useRef(false);

  const handleEditingChange = useCallback((nextEditing: boolean) => {
    if (nextEditing) {
      hasOpenedViaClickRef.current = true;
      setEditing(true);
    }
  }, []);

  const handleSaveAll = useCallback(() => {
    handleSave();
    setEditing(false);
  }, [handleSave]);

  const handleCancelAll = useCallback(() => {
    handleCancel();
    setEditing(false);
  }, [handleCancel]);

  const countryListOptions = useCountryListOptions();

  return (
    <InlineLabeled
      extra={disabled ? <FontAwesomeIcon className={block("lock")} icon={faLock} /> : undefined}
      label={label}
      labelClassName={cn(block("label"), labelClassName)}
      labelPosition={labelPosition}
      required={required}
    >
      <Row className={block()}>
        {extra && <Col span={24}>{extra}</Col>}
        <Col span={24}>
          <InlineTextField
            autoFocus={defaultEditing && !hasOpenedViaClickRef.current}
            disabled={disabled}
            editing={editing}
            onChange={handleAddressChange}
            onEditingChange={handleEditingChange}
            placeholder={formatMessage("ui.manualAddress.street")}
            showFooterButtons={false}
            suffix={
              findMeLoading ? (
                <LoadingSpinner mini />
              ) : !disabled ? (
                <Tooltip title={formatMessage("ui.address.findMe.tooltip")}>
                  <FontAwesomeIcon
                    className={block("find-me-icon")}
                    icon={faLocationArrow}
                    onClick={handleFindMyLocation}
                    size="lg"
                  />
                </Tooltip>
              ) : undefined
            }
            value={address?.address}
          />
        </Col>
        <Col span={24}>
          <InlineSelectField
            allowClear={canClearCountry}
            className={block("country-dropdown")}
            clearIcon={<FontAwesomeIcon icon={faTimesCircle} />}
            disabled={disabled}
            dropdownMatchSelectWidth={false}
            editing={editing}
            onChange={handleCountryCodeChange}
            onEditingChange={handleEditingChange}
            optionFilterProp="text"
            options={countryListOptions}
            placeholder={formatMessage("ui.manualAddress.country")}
            showFooterButtons={false}
            showSearch
            value={address?.countryCode}
          />
        </Col>
        <Col span={24}>
          <Row gutter={layout.spacerS}>
            <Col span={12}>
              <InlineTextField
                disabled={disabled}
                editing={editing}
                onChange={handleCityChange}
                onEditingChange={handleEditingChange}
                placeholder={formatMessage("ui.manualAddress.city")}
                showFooterButtons={false}
                value={address?.city}
              />
            </Col>
            <Col span={12}>
              <RegionField
                countryCode={address?.countryCode}
                disabled={disabled}
                editing={editing}
                onChange={handleRegionChange}
                onEditingChange={handleEditingChange}
                value={address}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <InlineTextField
            disabled={disabled}
            editing={editing}
            onChange={handlePostalCodeChange}
            onEditingChange={handleEditingChange}
            placeholder={formatMessage("ui.manualAddress.postalCode")}
            showFooterButtons={false}
            value={address?.postalCode}
          />
        </Col>
        {!disabled && (
          <Col span={24}>
            <Button
              className={block("search-manual-button")}
              onClick={onToggleManualAddress}
              type="link"
            >
              {formatMessage("ui.manualAddress.searchGoogle")}
            </Button>
          </Col>
        )}
        {editing && !disabled && (
          <Col span={24}>
            <Row justify="space-between">
              <span>{isAddressChanged ? formatMessage("ui.manualAddress.saveAllFields") : ""}</span>
              <InlineButtons onCancel={handleCancelAll} onSave={handleSaveAll} />
            </Row>
          </Col>
        )}
      </Row>
    </InlineLabeled>
  );
};

export default ManualAddress;
